import {type InfiniteData, useInfiniteQuery} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import type {ProjectsResponse} from './types'

interface UseProjectsOptions {
  owner: string
  repo: string
  state: 'open' | 'closed'
  query?: string
}

interface PageParam {
  cursor: string | null
  sortQueryCursor: string | null
}

const INITIAL_PAGE_PARAM: PageParam = {cursor: null, sortQueryCursor: null}

export function useProjects({owner, repo, state, query}: UseProjectsOptions) {
  return useInfiniteQuery<ProjectsResponse, Error, InfiniteData<ProjectsResponse>, unknown[], PageParam>({
    queryKey: ['repo-projects', owner, repo, state, query ?? ''],
    initialPageParam: INITIAL_PAGE_PARAM,
    queryFn: async ({pageParam}) => {
      const baseQuery = state === 'closed' ? 'is:closed' : 'is:open'
      const fullQuery = query ? `${baseQuery} ${query}` : baseQuery
      const params = new URLSearchParams({query: fullQuery})
      if (pageParam.cursor) {
        params.set('cursor', pageParam.cursor)
      }
      if (pageParam.sortQueryCursor) {
        params.set('sort_query_cursor', pageParam.sortQueryCursor)
      }
      const response = await verifiedFetchJSON(`/${owner}/${repo}/projects?${params.toString()}`)
      if (!response.ok) {
        throw new Error(`Failed to fetch projects: ${response.status}`)
      }
      return (await response.json()) as ProjectsResponse
    },
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? {cursor: lastPage.cursor, sortQueryCursor: lastPage.sortQueryCursor} : undefined,
  })
}
